import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import StatementValidationReport from "@/components/StatementValidationReport";

/**
 * Statement Library — audit-view grid of every bank statement certification
 * across every period.
 *
 * • Header cards: quick counts by verdict (auto-cert / override / failed / pending)
 * • Filter chips: verdict + period + free-text search on file name / period label
 * • Table row: click to open the full validation report inline
 *
 * Powered by GET /api/statement-library.
 */

const STATUS_META = {
  certified_automatic:  { label: "Auto-Certified",   cls: "status-ok",   emoji: "✅" },
  certified_override:   { label: "Override",         cls: "status-warn", emoji: "⚠" },
  validation_failed:    { label: "Failed",           cls: "status-bad",  emoji: "❌" },
  extraction_failed:    { label: "Extract Failed",   cls: "status-bad",  emoji: "❌" },
  pending:              { label: "Pending",          cls: "status-idle", emoji: "⏳" },
  extracting:           { label: "Extracting…",      cls: "status-idle", emoji: "⏳" },
  validating:           { label: "Validating…",      cls: "status-idle", emoji: "⏳" },
};

const FILTERS = [
  { key: "all",                  label: "All" },
  { key: "certified_automatic",  label: "Auto-Certified" },
  { key: "certified_override",   label: "Override" },
  { key: "validation_failed",    label: "Failed" },
  { key: "extraction_failed",    label: "Extract Failed" },
  { key: "pending",              label: "Pending" },
];


function VerdictBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", cls: "status-idle", emoji: "•" };
  return (
    <span className={`lib-badge ${meta.cls}`} data-status={status} data-testid={`lib-verdict-${status}`}>
      <span className="lib-badge-emoji" aria-hidden>{meta.emoji}</span>
      {meta.label}
    </span>
  );
}


function SummaryCard({ label, value, tone, testId, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`lib-summary-card tone-${tone}${active ? " active" : ""}`}>
      <div className="lib-summary-value">{value}</div>
      <div className="lib-summary-label">{label}</div>
    </button>
  );
}


export default function StatementLibrary() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [expandedDocId, setExpandedDocId] = useState(null);
  const [expandedReport, setExpandedReport] = useState(null);
  const [expandedLoading, setExpandedLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const d = await api.statementLibrary();
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];
  const summary = data?.summary || {};

  const filtered = useMemo(() => {
    let out = rows;
    if (filter !== "all") out = out.filter(r => r.status === filter);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      out = out.filter(r =>
        (r.file_name || "").toLowerCase().includes(needle) ||
        (r.period_label || "").toLowerCase().includes(needle) ||
        (r.override_reason || "").toLowerCase().includes(needle),
      );
    }
    return out;
  }, [rows, filter, q]);

  async function toggleExpand(row) {
    if (expandedDocId === row.document_id) {
      setExpandedDocId(null);
      setExpandedReport(null);
      return;
    }
    setExpandedDocId(row.document_id);
    setExpandedReport(null);
    setExpandedLoading(true);
    try {
      const rep = await api.statementReport(row.document_id);
      setExpandedReport(rep);
    } catch (e) {
      setExpandedReport({ error: e.message });
    } finally {
      setExpandedLoading(false);
    }
  }

  return (
    <div data-testid="page-statement-library">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 12 }}>
        <div>
          <div className="heading h1">Statement Library</div>
          <div className="muted" style={{ marginTop: 4, fontSize: 13 }}>
            Every bank statement certification, across every period. Click a row
            to expand the validation report for that statement.
          </div>
        </div>
        <button
          className="btn ghost btn-sm"
          onClick={load}
          data-testid="lib-refresh">
          Refresh
        </button>
      </div>

      {err && (
        <div className="cert-error-card" style={{ marginTop: 12 }}>
          Failed to load library: {err}
        </div>
      )}

      {loading && !data && (
        <div className="muted" style={{ marginTop: 20 }}>Loading…</div>
      )}

      {data && (
        <>
          <div className="lib-summary-row">
            <SummaryCard label="Total"          value={summary.total || 0}                tone="idle" testId="lib-summary-total"    active={filter === "all"}                 onClick={() => setFilter("all")} />
            <SummaryCard label="Auto-Certified" value={summary.certified_automatic || 0}  tone="ok"   testId="lib-summary-auto"     active={filter === "certified_automatic"} onClick={() => setFilter("certified_automatic")} />
            <SummaryCard label="Override"       value={summary.certified_override || 0}   tone="warn" testId="lib-summary-override" active={filter === "certified_override"}  onClick={() => setFilter("certified_override")} />
            <SummaryCard label="Failed"         value={summary.validation_failed || 0}    tone="bad"  testId="lib-summary-failed"   active={filter === "validation_failed"}   onClick={() => setFilter("validation_failed")} />
            <SummaryCard label="Extract Failed" value={summary.extraction_failed || 0}    tone="bad"  testId="lib-summary-extract"  active={filter === "extraction_failed"}   onClick={() => setFilter("extraction_failed")} />
            <SummaryCard label="Pending"        value={summary.pending || 0}              tone="idle" testId="lib-summary-pending"  active={filter === "pending"}             onClick={() => setFilter("pending")} />
          </div>

          <div className="lib-toolbar">
            <div className="lib-filter-chips">
              {FILTERS.map(f => (
                <button
                  key={f.key}
                  type="button"
                  data-testid={`lib-filter-${f.key}`}
                  onClick={() => setFilter(f.key)}
                  className={`lib-chip${filter === f.key ? " active" : ""}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              placeholder="Search file name, period, or override reason…"
              value={q}
              onChange={e => setQ(e.target.value)}
              data-testid="lib-search"
              className="lib-search"
            />
          </div>

          {filtered.length === 0 ? (
            <div className="empty" style={{ marginTop: 16 }}>
              No statements match this filter.
            </div>
          ) : (
            <div className="lib-table-wrap">
              <table className="lib-table" data-testid="lib-table">
                <thead>
                  <tr>
                    <th>Verdict</th>
                    <th>Period</th>
                    <th>Statement Window</th>
                    <th style={{ textAlign: "right" }}>Opening</th>
                    <th style={{ textAlign: "right" }}>Closing</th>
                    <th style={{ textAlign: "right" }}>Txns</th>
                    <th style={{ textAlign: "right" }}>Exceptions</th>
                    <th>Controls</th>
                    <th>File</th>
                    <th>Certified</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(row => {
                    const expanded = expandedDocId === row.document_id;
                    return (
                      <React.Fragment key={row.document_id}>
                        <tr
                          className={`lib-row${expanded ? " expanded" : ""}`}
                          data-testid={`lib-row-${row.document_id}`}
                          onClick={() => toggleExpand(row)}>
                          <td><VerdictBadge status={row.status} /></td>
                          <td>
                            <Link
                              to={`/periods/${row.period_id}`}
                              onClick={e => e.stopPropagation()}
                              data-testid={`lib-period-link-${row.document_id}`}
                              className="link">
                              {row.period_label || "—"}
                            </Link>
                            <div className="muted" style={{ fontSize: 10 }}>{row.period_status}</div>
                          </td>
                          <td className="mono" style={{ fontSize: 12 }}>
                            {row.statement_period_start && row.statement_period_end
                              ? `${row.statement_period_start} → ${row.statement_period_end}`
                              : <span className="muted">—</span>}
                          </td>
                          <td className="mono" style={{ textAlign: "right", fontSize: 12 }}>
                            {row.opening_balance != null ? fmt.currency(row.opening_balance) : "—"}
                          </td>
                          <td className="mono" style={{ textAlign: "right", fontSize: 12 }}>
                            {row.closing_balance != null ? fmt.currency(row.closing_balance) : "—"}
                          </td>
                          <td className="mono" style={{ textAlign: "right" }}>
                            {row.transaction_count ?? "—"}
                          </td>
                          <td className="mono" style={{ textAlign: "right" }}>
                            {row.exception_count > 0
                              ? <span className="lib-count-bad">{row.exception_count}</span>
                              : <span className="muted">0</span>}
                          </td>
                          <td>
                            <div className="lib-tri">
                              <span className={`lib-tri-dot ${row.controls_pass === true ? "ok" : row.controls_pass === false ? "bad" : "idle"}`} title="Controls" />
                              <span className={`lib-tri-dot ${row.matching_pass === true ? "ok" : row.matching_pass === false ? "bad" : "idle"}`} title="Matching" />
                              <span className={`lib-tri-dot ${row.running_balance_pass === true ? "ok" : row.running_balance_pass === false ? "bad" : "idle"}`} title="Running Balance" />
                            </div>
                          </td>
                          <td style={{ fontSize: 12, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {row.file_name || <span className="muted">—</span>}
                          </td>
                          <td style={{ fontSize: 11 }}>
                            {row.created_at ? fmt.dateTime(row.created_at) : <span className="muted">—</span>}
                            {row.certified_by && (
                              <div className="muted" style={{ fontSize: 10 }}>by {row.certified_by}</div>
                            )}
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <span className="muted" style={{ fontSize: 11 }}>{expanded ? "▼" : "▶"}</span>
                          </td>
                        </tr>
                        {expanded && (
                          <tr data-testid={`lib-expanded-${row.document_id}`} className="lib-expanded-row">
                            <td colSpan={11}>
                              {expandedLoading && <div className="muted" style={{ padding: 12 }}>Loading report…</div>}
                              {expandedReport?.error && (
                                <div className="cert-error-card">Failed: {expandedReport.error}</div>
                              )}
                              {expandedReport && !expandedReport.error && (
                                <div style={{ padding: "12px 4px" }}>
                                  <StatementValidationReport report={expandedReport} />
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
